/**
 * Integrations API Service
 *
 * CRUD and sync operations for integrations aligned with OpenAPI specification.
 * @see http://localhost:8080/docs-json
 */

import api from '@/core/api'
import type {
    IntegrationListResponse,
    IntegrationStatusListResponse,
    IntegrationSingleResponse,
    IntegrationRegisteredResponse,
    IntegrationToggleResponse,
    IntegrationSettingsResponse,
    RegisterIntegrationDto,
    UpdateSecretsDto,
    SyncQueuedResponse,
    SyncHistoryResponse,
    SyncStatusResponse,
    BulkSyncFilters,
    CreditUsage,
    IdentityMapping,
    AcumaticaTestConnectionDto,
    AcumaticaTestConnectionResult,
    AcumaticaSettings,
    AcumaticaEntityMapping,
    AcumaticaBranchConfig,
    AcumaticaMappingsResponse,
    AcumaticaSyncRequest,
    AcumaticaSyncResponse,
    AcumaticaIdentitySyncResponse,
    AcumaticaSchemaField,
    AcumaticaGIResponse,
} from './types'
import type { MeetingEvent } from '@/modules/fleet/api/types'

// ─── Integration Management ─────────────────────────────────────────

export const integrationService = {
    /**
     * List all integrations for the organization
     * @see OpenAPI: GET /integrations
     */
    async list(): Promise<IntegrationListResponse> {
        const { data } = await api.get<IntegrationListResponse>('/integrations')
        return data
    },

    /**
     * List integration status for all authenticated users (slim payload)
     * @see OpenAPI: GET /integrations/status
     */
    async listStatus(): Promise<IntegrationStatusListResponse> {
        const { data } = await api.get<IntegrationStatusListResponse>('/integrations/status')
        return data
    },

    /**
     * Get a single integration by system slug
     * @see OpenAPI: GET /integrations/{system}
     */
    async getBySystem(system: string): Promise<IntegrationSingleResponse> {
        const { data } = await api.get<IntegrationSingleResponse>(`/integrations/${system}`)
        return data
    },

    /**
     * Register a new integration
     * @see OpenAPI: POST /integrations/{system}
     */
    async register(system: string, payload: RegisterIntegrationDto): Promise<IntegrationRegisteredResponse> {
        const { data } = await api.post<IntegrationRegisteredResponse>(`/integrations/${system}`, payload)
        return data
    },

    /**
     * Enable an integration
     * @see OpenAPI: POST /integrations/{system}/enable
     */
    async enable(system: string): Promise<IntegrationToggleResponse> {
        const { data } = await api.post<IntegrationToggleResponse>(`/integrations/${system}/enable`)
        return data
    },

    /**
     * Disable an integration
     * @see OpenAPI: POST /integrations/{system}/disable
     */
    async disable(system: string): Promise<IntegrationToggleResponse> {
        const { data } = await api.post<IntegrationToggleResponse>(`/integrations/${system}/disable`)
        return data
    },

    /**
     * Update integration settings (shallow-merge)
     * @see OpenAPI: PATCH /integrations/{system}/settings
     */
    async updateSettings(system: string, settings: Record<string, unknown>): Promise<IntegrationSettingsResponse> {
        const { data } = await api.patch<IntegrationSettingsResponse>(`/integrations/${system}/settings`, settings)
        return data
    },

    /**
     * Replace integration secrets
     * @see OpenAPI: PATCH /integrations/{system}/secrets
     */
    async updateSecrets(system: string, secrets: UpdateSecretsDto): Promise<{ ok: boolean }> {
        const { data } = await api.patch<{ ok: boolean }>(`/integrations/${system}/secrets`, secrets)
        return data
    },

    // ─── Identity Mappings ────────────────────────────────────────────

    /**
     * Get identity mappings for a specific platform user.
     * Returns mappings across all integrated systems (e.g., msgraph).
     * @see GET /integrations/identity-mappings/users/:userId
     */
    async getUserMappings(userId: string): Promise<IdentityMapping[]> {
        const { data } = await api.get<{ ok: boolean; data: IdentityMapping[] }>(
            `/integrations/identity-mappings/users/${userId}`,
        )
        return data.data ?? []
    },

    // ─── Meetings ───────────────────────────────────────────────

    /**
     * Get meetings for a platform user by UUID.
     * @see GET /integrations/users/:userId/meetings
     */
    async getUserMeetings(userId: string, from: string, to?: string): Promise<MeetingEvent[]> {
        const { data } = await api.get<{ ok: boolean; data: { meetings: MeetingEvent[] } }>(
            `/integrations/users/${userId}/meetings`,
            { params: { from, to: to || from }, _skipNotFound: true } as any,
        )
        return data.data?.meetings ?? []
    },
}

// ─── Apollo Sync ─────────────────────────────────────────────────────

export const apolloSyncService = {
    /**
     * Sync a single property to Apollo (async — returns 202)
     * @see OpenAPI: POST /integrations/apollo/sync/properties/{id}
     */
    async syncProperty(propertyId: string, options?: { forceSync?: boolean }): Promise<SyncQueuedResponse> {
        let url = `/integrations/apollo/sync/properties/${propertyId}`
        if (options?.forceSync) url += `?forceSync=true`
        const { data } = await api.post<SyncQueuedResponse>(url)
        return data
    },

    /**
     * Bulk sync properties to Apollo (async — returns 202)
     * @see OpenAPI: POST /integrations/apollo/sync/properties
     */
    async bulkSync(filters?: BulkSyncFilters): Promise<SyncQueuedResponse> {
        const { data } = await api.post<SyncQueuedResponse>('/integrations/apollo/sync/properties', filters || {})
        return data
    },

    /**
     * Bulk sync specific properties to Apollo by IDs (async — returns 202)
     * Sends a single request instead of N individual syncProperty() calls.
     * @see OpenAPI: POST /integrations/apollo/sync/properties
     */
    async syncBulk(payload: { propertyIds?: string[], forceSync?: boolean, [key: string]: any }): Promise<SyncQueuedResponse> {
        const { data } = await api.post<SyncQueuedResponse>(
            '/integrations/apollo/sync/properties',
            payload
        )
        return data
    },

    /**
     * Unlink a property from Apollo — removes the sync record.
     * @see OpenAPI: DELETE /integrations/apollo/sync/properties/{propertyId}
     */
    async unlink(propertyId: string): Promise<{ ok: boolean }> {
        const { data } = await api.delete<{ ok: boolean }>(`/integrations/apollo/sync/properties/${propertyId}`)
        return data
    },

    /**
     * Get sync history for a specific property
     * @see OpenAPI: GET /integrations/apollo/sync/properties/{id}/history
     */
    async getSyncHistory(propertyId: string): Promise<SyncHistoryResponse> {
        const { data } = await api.get<SyncHistoryResponse>(`/integrations/apollo/sync/properties/${propertyId}/history`)
        return data
    },

    /**
     * Get org-wide sync status summary
     * @see OpenAPI: GET /integrations/apollo/sync/status
     */
    async getSyncStatus(): Promise<SyncStatusResponse> {
        const { data } = await api.get<SyncStatusResponse>('/integrations/apollo/sync/status')
        return data
    },

    /**
     * Get all sync history across all properties
     * @see OpenAPI: GET /integrations/apollo/sync/history
     */
    async getAllSyncHistory(params?: { status?: string; limit?: number; offset?: number }): Promise<SyncHistoryResponse> {
        const { data } = await api.get<SyncHistoryResponse>('/integrations/apollo/sync/history', { params })
        return data
    },
}

// ─── Apollo Contact Sync ─────────────────────────────────────────────

export const apolloContactSyncService = {
    /**
     * Sync contacts for all synced properties
     * @see OpenAPI: POST /integrations/apollo/sync/contacts
     */
    async syncAll(): Promise<SyncQueuedResponse> {
        const { data } = await api.post<SyncQueuedResponse>('/integrations/apollo/sync/contacts')
        return data
    },

    /**
     * Sync contacts for a specific property
     * @see OpenAPI: POST /integrations/apollo/sync/contacts/property/{id}
     */
    async syncForProperty(propertyId: string): Promise<SyncQueuedResponse> {
        const { data } = await api.post<SyncQueuedResponse>(`/integrations/apollo/sync/contacts/property/${propertyId}`)
        return data
    },

    /**
     * Sync contacts for a specific tenant
     * @see OpenAPI: POST /integrations/apollo/sync/contacts/tenant/{id}
     */
    async syncForTenant(tenantId: string): Promise<SyncQueuedResponse> {
        const { data } = await api.post<SyncQueuedResponse>(`/integrations/apollo/sync/contacts/tenant/${tenantId}`)
        return data
    },

    /**
     * Get contact sync status summary
     * @see OpenAPI: GET /integrations/apollo/sync/contacts/status
     */
    async getStatus(): Promise<SyncStatusResponse> {
        const { data } = await api.get<SyncStatusResponse>('/integrations/apollo/sync/contacts/status')
        return data
    },

    /**
     * Get daily credit usage and budget
     * @see OpenAPI: GET /integrations/apollo/credits/usage
     */
    async getCreditUsage(): Promise<{ ok: boolean; data: CreditUsage }> {
        const { data } = await api.get<{ ok: boolean; data: CreditUsage }>('/integrations/apollo/credits/usage')
        return data
    },
}

// ─── Apollo Enrichment ───────────────────────────────────────────────

export const apolloEnrichmentService = {
    /**
     * Enrich a contact's email via Apollo.
     * Returns raw Axios response so the caller can check status:
     *   200 = instant (native mode)
     *   202 = pending (waterfall mode)
     *   400 = budget exhausted / missing name/domain / already pending
     * @see OpenAPI: POST /integrations/apollo/contacts/{contactId}/enrich/email
     */
    async enrichEmail(contactId: string, forceOverwrite?: string) {
        const params = forceOverwrite ? { forceOverwrite } : undefined
        return api.post(`/integrations/apollo/contacts/${contactId}/enrich/email`, null, { params })
    },

    /**
     * Enrich a contact's phone via Apollo.
     * Always returns 202 (async).
     *   400 = budget exhausted / missing name/domain / already pending
     * @see OpenAPI: POST /integrations/apollo/contacts/{contactId}/enrich/phone
     */
    async enrichPhone(contactId: string, forceOverwrite?: string) {
        const params = forceOverwrite ? { forceOverwrite } : undefined
        return api.post(`/integrations/apollo/contacts/${contactId}/enrich/phone`, null, { params })
    },
}

// ─── Acumatica ───────────────────────────────────────────────────────

export const acumaticaService = {
    /**
     * Test Acumatica credentials.
     * When called without arguments, tests organization's saved credentials.
     * @see POST /integrations/acumatica/test
     */
    async testConnection(dto?: Partial<AcumaticaTestConnectionDto>): Promise<{ ok: boolean; data: AcumaticaTestConnectionResult }> {
        const { data } = await api.post<{ ok: boolean; data: AcumaticaTestConnectionResult }>(
            '/integrations/acumatica/test',
            dto ?? {},
        )
        return data
    },

    /**
     * List discovered Acumatica branches.
     * @see GET /integrations/acumatica/branches
     */
    async getBranches(): Promise<{ ok: boolean; data: string[] }> {
        const { data } = await api.get<{ ok: boolean; data: string[] }>('/integrations/acumatica/branches')
        return data
    },

    /**
     * Discover standard + custom DAC entity fields.
     * @see GET /integrations/acumatica/schema/:entityName
     */
    async getSchema(entityName: string): Promise<{ ok: boolean; data: AcumaticaSchemaField[] }> {
        const { data } = await api.get<{ ok: boolean; data: AcumaticaSchemaField[] }>(
            `/integrations/acumatica/schema/${encodeURIComponent(entityName)}`,
        )
        return data
    },

    /**
     * Discover UDF (user-defined) fields for an entity.
     * @see GET /integrations/acumatica/schema/:entityName/ad-hoc
     */
    async getAdHocSchema(entityName: string): Promise<{ ok: boolean; data: AcumaticaSchemaField[] }> {
        const { data } = await api.get<{ ok: boolean; data: AcumaticaSchemaField[] }>(
            `/integrations/acumatica/schema/${encodeURIComponent(entityName)}/ad-hoc`,
        )
        return data
    },

    /**
     * Get current entity/field/branch mappings.
     * @see GET /integrations/acumatica/mappings
     */
    async getMappings(): Promise<AcumaticaMappingsResponse> {
        const { data } = await api.get<AcumaticaMappingsResponse>('/integrations/acumatica/mappings')
        return data
    },

    /**
     * Update default entity/field mappings.
     * @see PUT /integrations/acumatica/mappings
     */
    async updateMappings(payload: { entityMappings: Partial<Record<string, Partial<AcumaticaEntityMapping>>> }): Promise<{ ok: boolean }> {
        const { data } = await api.put<{ ok: boolean }>('/integrations/acumatica/mappings', payload)
        return data
    },

    /**
     * Update branch-specific mapping overrides.
     * @see PUT /integrations/acumatica/branches/:branchId/mappings
     */
    async updateBranchMappings(branchId: string, payload: AcumaticaBranchConfig): Promise<{ ok: boolean }> {
        const { data } = await api.put<{ ok: boolean }>(
            `/integrations/acumatica/branches/${encodeURIComponent(branchId)}/mappings`,
            payload,
        )
        return data
    },

    /**
     * Update general integration settings (PATCH semantics).
     * @see PUT /integrations/acumatica/settings
     */
    async updateSettings(payload: AcumaticaSettings): Promise<{ ok: boolean }> {
        const { data } = await api.put<{ ok: boolean }>('/integrations/acumatica/settings', payload)
        return data
    },

    /**
     * Query a Generic Inquiry (read-only preview).
     * @see GET /integrations/acumatica/gi/:giName
     */
    async queryGI(giName: string, params?: { filter?: string; top?: number; skip?: number; branch?: string }): Promise<AcumaticaGIResponse> {
        const { data } = await api.get<AcumaticaGIResponse>(
            `/integrations/acumatica/gi/${encodeURIComponent(giName)}`,
            { params },
        )
        return data
    },

    /**
     * Trigger manual sync for specific entities.
     * @see POST /integrations/acumatica/sync
     */
    async triggerSync(payload: AcumaticaSyncRequest): Promise<AcumaticaSyncResponse> {
        const { data } = await api.post<AcumaticaSyncResponse>('/integrations/acumatica/sync', payload)
        return data
    },

    /**
     * Sync user ↔ employee identity mappings.
     * @see POST /integrations/acumatica/sync-identity
     */
    async syncIdentity(): Promise<AcumaticaIdentitySyncResponse> {
        const { data } = await api.post<AcumaticaIdentitySyncResponse>('/integrations/acumatica/sync-identity')
        return data
    },
}

